import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { Helmet } from 'react-helmet-async';

export default function LeaveReview() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  
  const [item, setItem] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      try {
        const itemSnap = await getDoc(doc(db, 'listings', itemId));
        if (itemSnap.exists()) {
          const itemData = itemSnap.data();
          setItem({ id: itemSnap.id, ...itemData });

          const sellerSnap = await getDoc(doc(db, 'users', itemData.sellerId));
          if (sellerSnap.exists()) {
            setSeller(sellerSnap.data());
          }
        }
      } catch (error) {
        console.error("Error fetching details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactionDetails();
  }, [itemId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating || !reviewText.trim()) return alert("Please provide a rating and a brief review.");
    setIsSubmitting(true);

    try {
      // Write to the global reviews collection
      await addDoc(collection(db, 'reviews'), {
        sellerId: item.sellerId,
        buyerId: auth.currentUser.uid,
        buyerName: auth.currentUser.email.split('@')[0], // Fallback to email prefix
        itemId: item.id,
        itemTitle: item.title,
        rating: rating,
        text: reviewText,
        createdAt: serverTimestamp()
      });

      alert("Review submitted successfully! Thank you.");
      navigate(`/seller/${item.sellerId}`); // Bounce them to the seller's profile to see their review live
    } catch (error) {
      console.error("Error submitting review:", error);
      alert("Failed to submit review.");
      setIsSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: '100px', textAlign: 'center', color: '#64748b' }}>Loading transaction...</div>;
  if (!item || !seller) return <div style={{ padding: '100px', textAlign: 'center', color: '#ef4444' }}>Transaction not found.</div>;

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: '"Inter", sans-serif' }}>
      <Helmet>
        <title>Leave a Review | Nundinaria</title>
      </Helmet>

      <div style={{ width: '100%', maxWidth: '500px', backgroundColor: 'white', borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        
        <div style={{ padding: '30px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f1f5f9' }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>Rate your experience</h2>
          <p style={{ margin: 0, color: '#475569', fontSize: '15px' }}>
            Purchased: <strong>{item.title}</strong><br/>
            Sold by: <strong>{seller.email?.split('@')[0]}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '30px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* STAR RATING INTERFACE */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>Overall Rating</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '40px',
                    cursor: 'pointer',
                    color: star <= (hoverRating || rating) ? '#fbbf24' : '#e2e8f0',
                    transition: 'color 0.2s',
                    padding: 0,
                    lineHeight: 1
                  }}
                >
                  ★
                </button>
              ))}
            </div>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a' }}>
              {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great' : rating === 3 ? 'Okay' : rating === 2 ? 'Needs Improvement' : rating === 1 ? 'Terrible' : ''}
            </span>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '13px', color: '#0f172a', textTransform: 'uppercase' }}>Written Review</label>
            <textarea 
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Was the item as described? How was the communication?"
              required
              rows="4"
              style={{ width: '100%', padding: '16px', borderRadius: '12px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', fontSize: '15px', color: '#0f172a', boxSizing: 'border-box', outline: 'none', resize: 'vertical' }}
              onFocus={e => { e.target.style.borderColor = '#4f46e5'; e.target.style.backgroundColor = 'white'; }}
              onBlur={e => { e.target.style.borderColor = '#cbd5e1'; e.target.style.backgroundColor = '#f8fafc'; }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
            <button type="button" onClick={() => navigate(-1)} style={{ flex: 1, padding: '16px', backgroundColor: 'transparent', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer' }}>Cancel</button>
            
            <button type="submit" disabled={isSubmitting} style={{ flex: 1, padding: '16px', backgroundColor: isSubmitting ? '#94a3b8' : '#4f46e5', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: isSubmitting ? 'not-allowed' : 'pointer', boxShadow: isSubmitting ? 'none' : '0 4px 6px -1px rgba(79, 70, 229, 0.3)' }}>
              {isSubmitting ? 'Submitting...' : 'Post Review'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}